export const PILOTO_STORAGE_KEY = 'hydrodata.dwight.piloto-opus.v1';
export const TETO_USD = 15;
export type Rotulo = 'A' | 'B';
export interface MetricasVersao {
  tokens_in: number | null; tokens_out: number | null;
  custo_real_usd: number | null; latencia_ms: number | null;
}
export interface CasoPiloto {
  caso_id: string; codigo: string; tipo: string; status: string; area?: string;
  tem_revisao?: boolean; job_id?: string; job_status?: string;
}
export interface NotaVersao {
  fidelidade: number; completude: number; coerencia: number; clareza: number;
  erro_critico: boolean; aceitavel_primeira_revisao: boolean; minutos_revisao: number;
}
export interface AvaliacaoPiloto {
  A: NotaVersao; B: NotaVersao;
  vencedor: Rotulo | 'empate' | 'nenhuma'; motivo: string; avaliado_em: string;
}
export interface RegistroPiloto {
  caso_id: string; job_id?: string; status: string; iniciado_em: string;
  custo_reservado_usd: number; custo_real_usd?: number;
  tokens?: unknown; latencia_ms?: unknown; avaliacao?: AvaliacaoPiloto;
  modelos_por_rotulo?: Record<Rotulo, string>; metricas_por_rotulo?: Record<Rotulo, MetricasVersao>;
}
export interface MemoriaPiloto {
  versao: 1; acumulado_servidor_usd: number; registros: RegistroPiloto[];
}
export const memoriaVazia = (): MemoriaPiloto => ({ versao: 1, acumulado_servidor_usd: 0, registros: [] });
const custoValido = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n >= 0;

// Não transforma memória corrompida em orçamento zero.
export function lerMemoriaPiloto(raw: string | null): MemoriaPiloto {
  if (!raw) return memoriaVazia();
  const m = JSON.parse(raw);
  if (m?.versao !== 1 || !custoValido(m.acumulado_servidor_usd) || !Array.isArray(m.registros)
    || m.registros.some((r: RegistroPiloto) => typeof r.caso_id !== 'string' || typeof r.status !== 'string'
      || !custoValido(r.custo_reservado_usd) || (r.custo_real_usd !== undefined && !custoValido(r.custo_real_usd)))) {
    throw new Error('O registro local do orçamento não pôde ser conferido. Novas execuções estão bloqueadas.');
  }
  return m;
}
export function custoComprometido(m: MemoriaPiloto): number {
  return Math.max(m.acumulado_servidor_usd,
    m.registros.reduce((s, r) => s + (r.custo_real_usd ?? r.custo_reservado_usd), 0));
}
export function reconciliarCasos(m: MemoriaPiloto, casos: CasoPiloto[]): RegistroPiloto[] {
  return casos.reduce((rs, c) => {
    if (!c.job_id) return rs.filter(r => r.caso_id !== c.caso_id || !!r.job_id);
    const anterior = rs.find(r => r.caso_id === c.caso_id);
    if (anterior?.job_id) return rs;
    return [...rs.filter(r => r.caso_id !== c.caso_id), {
      ...anterior, caso_id: c.caso_id, job_id: c.job_id, status: c.job_status || 'pendente',
      iniciado_em: anterior?.iniciado_em || new Date().toISOString(), custo_reservado_usd: anterior?.custo_reservado_usd || 0,
    }];
  }, m.registros);
}
export function podeIniciar(m: MemoriaPiloto, casoId: string): boolean {
  return custoComprometido(m) < TETO_USD && m.registros.length < 10
    && !m.registros.some(r => r.caso_id === casoId || !r.job_id
      || !['concluido', 'erro', 'falhou', 'falha'].includes(r.status)
      || (r.status === 'concluido' && !r.avaliacao));
}
export function avaliacaoValida(a: AvaliacaoPiloto): boolean {
  return !!a && ['A', 'B', 'empate', 'nenhuma'].includes(a.vencedor) && !!a.motivo
    && (['A', 'B'] as Rotulo[]).every(l => {
      const n = a[l];
      return n && ['fidelidade', 'completude', 'coerencia', 'clareza'].every(k => {
        const v = n[k as keyof NotaVersao];
        return typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= 5;
      }) && typeof n.erro_critico === 'boolean' && typeof n.aceitavel_primeira_revisao === 'boolean' && custoValido(n.minutos_revisao);
    });
}
export function exportarPiloto(m: MemoriaPiloto) {
  // Lista explícita: jamais exportar payloads, propostas, prompts ou reveal_token.
  return {
    piloto: 'dwight-opus', versao: 1, exportado_em: new Date().toISOString(), teto_usd: TETO_USD,
    custo_comprometido_usd: custoComprometido(m),
    custo_real_conhecido_usd: m.registros.reduce((s, r) => s + (r.custo_real_usd ?? 0), 0),
    registros: m.registros.map(r => ({
      caso_id: r.caso_id, job_id: r.job_id, status: r.status, iniciado_em: r.iniciado_em,
      custo_reservado_usd: r.custo_reservado_usd, custo_real_usd: r.custo_real_usd,
      tokens: r.tokens, latencia_ms: r.latencia_ms, avaliacao: r.avaliacao,
      metricas_por_rotulo: r.avaliacao ? r.metricas_por_rotulo : undefined,
      modelos_por_rotulo: r.avaliacao ? r.modelos_por_rotulo : undefined,
    })),
  };
}
